import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createAdminManager,
  deleteAdminManager,
  DuplicateManagerError,
  fetchAdminManagers,
  fetchManagementTrades,
  ManagerInUseError,
  updateAdminManager,
  type ICreateManagerInput,
} from '@apis/adminManagers';
import type { IToast } from '@components/ui/Toasts/useToasts';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

const adminManagersKey = (siteId: string) => ['admin', 'managers', siteId] as const;
const adminManagementTradesKey = (siteId: string) => ['admin', 'managers', siteId, 'trades'] as const;

type ToastPush = (toast: Omit<IToast, 'id'>) => void;

export function useAdminManagers(siteId: string | null) {
  return useQuery({
    queryKey: adminManagersKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchAdminManagers(siteId!, signal),
    enabled: !!siteId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useManagementTrades(siteId: string | null) {
  return useQuery({
    queryKey: adminManagementTradesKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchManagementTrades(siteId!, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useCreateAdminManager(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (input: ICreateManagerInput) => createAdminManager(siteId!, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminManagersKey(siteId!) });
    },
    onError: (err) => {
      push({
        kind: 'error',
        title: t(
          err instanceof DuplicateManagerError
            ? 'adminManagement.duplicateError'
            : 'adminManagement.generalError',
        ),
      });
    },
  });
}

export function useUpdateAdminManager(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ managerId, phone }: { managerId: string; phone: string | null }) =>
      updateAdminManager(siteId!, managerId, { phone }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminManagersKey(siteId!) });
    },
    onError: () => {
      push({ kind: 'error', title: t('adminManagement.generalError') });
    },
  });
}

/**
 * Delete a manager. A 409 MANAGER_IN_USE is NOT toasted here — the delete
 * dialog renders the blocked message instead, so the error is rethrown for
 * the caller to branch on.
 */
export function useDeleteAdminManager(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (managerId: string) => deleteAdminManager(siteId!, managerId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminManagersKey(siteId!) });
    },
    onError: (err) => {
      if (err instanceof ManagerInUseError) return; // the dialog renders it
      push({ kind: 'error', title: t('adminManagement.generalError') });
    },
  });
}
